import React from 'react'
import PropTypes from 'prop-types'

import {
    Skeleton,
    Stack,
    useStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'

const getAccordionSkeletonLayout = (item, itemsCountKey = 'count') => {
    const itemCount = item?.[itemsCountKey] || item?.categories?.length || item?.items?.length || 0
    const hasImageCards = ['grid', 'carousel', 'single-image'].includes(
        item?.c_imageCardsLayoutInMobile
    )
    const isSingleImageCard = item?.c_imageCardsLayoutInMobile === 'single-image'
    const rowCount = isSingleImageCard
        ? Math.min(Math.max(itemCount - 1, 0), 8)
        : Math.min(Math.max(itemCount, 3), 8)

    return {
        hasImageCards,
        isSingleImageCard,
        rowCount,
        minHeight: isSingleImageCard
            ? undefined
            : `${Math.max(rowCount * 48 + (hasImageCards ? 235 : 24), 220)}px`
    }
}

// This component is used as a fallback skeleton when the nested accordion panel is loading.
// It determines the layout of the skeleton based on the presence of image cards and the number of items in the panel.
export const AccordionPanelSkeleton = ({initialDepth = 1, item, itemsCountKey, styles}) => {
    const {hasImageCards, isSingleImageCard, minHeight, rowCount} = getAccordionSkeletonLayout(
        item,
        itemsCountKey
    )
    const isNestedPanel = initialDepth >= 2

    return (
        <Stack
            {...styles.panelSkeleton?.container}
            gap={isSingleImageCard ? '5px' : styles.panelSkeleton?.container?.gap}
            marginLeft={isNestedPanel ? '30px' : '0px'}
            minHeight={minHeight}
            width={isSingleImageCard ? '351px' : styles.panelSkeleton?.container?.width}
            maxWidth={isNestedPanel ? 'calc(100% - 30px)' : '100%'}
            data-testid="mobile-nav-accordion-skeleton"
            data-depth={initialDepth}
        >
            {hasImageCards && (
                <Stack gap="5px">
                    <Skeleton
                        {...styles.panelSkeleton?.hero}
                        width={isSingleImageCard ? 'min(351px, 100%)' : '160px'}
                        height={isSingleImageCard ? '210px' : '194px'}
                    />
                    {isSingleImageCard && (
                        <Skeleton {...styles.panelSkeleton?.caption} width="45%" />
                    )}
                </Stack>
            )}
            {rowCount > 0 && (
                <Stack
                    {...styles.panelSkeleton?.rows}
                    marginTop={isSingleImageCard ? '25px' : '0px'}
                >
                    {Array.from({length: rowCount}).map((_, index) => (
                        <Skeleton
                            key={index}
                            {...styles.panelSkeleton?.row}
                            width={index % 3 === 2 ? '55%' : index % 2 ? '68%' : '78%'}
                        />
                    ))}
                </Stack>
            )}
        </Stack>
    )
}

AccordionPanelSkeleton.propTypes = {
    initialDepth: PropTypes.number,
    item: PropTypes.object,
    itemsCountKey: PropTypes.string,
    styles: PropTypes.object
}

export const NestedAccordionPanelSkeleton = ({initialDepth, item, itemsCountKey, styles}) => {
    const nestedAccordionStyles = useStyleConfig('NestedAccordion')

    return (
        <AccordionPanelSkeleton
            initialDepth={initialDepth}
            item={item}
            itemsCountKey={itemsCountKey}
            styles={styles || nestedAccordionStyles}
        />
    )
}

NestedAccordionPanelSkeleton.propTypes = {
    initialDepth: PropTypes.number,
    item: PropTypes.object,
    itemsCountKey: PropTypes.string,
    styles: PropTypes.object
}
